import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';

import { LoadingSpinner, PageContainer, Section } from '../../../shared/ui';
import { appendYowanPreviewQueryIfNeeded } from '../../../app/host/yowanPreviewRouting';
import { confirmYowanPasswordSetup } from '../app/yowanAuthClient';
const { yowanPageContent } = require('./pageContent');
const { getYowanRouteMeta } = require('../routes/manifest');

const collectPasswordSetupFieldErrors = (fieldErrors = {}) => {
  const newPasswordErrors = fieldErrors.new_password || fieldErrors.newPassword || [];

  if (Array.isArray(newPasswordErrors)) {
    return newPasswordErrors.map((message) => String(message)).filter(Boolean);
  }

  if (newPasswordErrors) {
    return [String(newPasswordErrors)];
  }

  return [];
};

const resolvePasswordSetupErrorMessage = (error, content) => {
  const responseData = error?.response?.data || {};
  const fieldErrorMessages = collectPasswordSetupFieldErrors(responseData.field_errors || responseData.fieldErrors);

  if (fieldErrorMessages.length > 0) {
    return [
      content.form.apiErrorMessages?.passwordValidationFailed ||
        responseData.message ||
        content.form.errorMessage,
      ...fieldErrorMessages,
    ].join('\n');
  }

  const reason = responseData.reason || responseData.code || '';
  const mappedMessage = content.form.apiErrorMessages?.[reason];

  if (mappedMessage) {
    return mappedMessage;
  }

  if (typeof responseData.message === 'string' && responseData.message) {
    return responseData.message;
  }

  return content.form.errorMessage;
};

const PasswordSetupConfirmPage = () => {
  const content = yowanPageContent['/password-setup/confirm'];
  const metadata = getYowanRouteMeta('/password-setup/confirm');
  const location = useLocation();
  const navigate = useNavigate();
  const tokenFromQuery = new URLSearchParams(location.search).get('token') || '';
  const [submitFeedback, setSubmitFeedback] = useState({ message: '', isError: false });
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm({
    defaultValues: {
      new_password: '',
      new_password_confirm: '',
    },
  });

  useEffect(() => {
    if (!metadata) {
      return;
    }

    document.title = metadata.title;

    if (metadata.description) {
      const descriptionTag = document.querySelector('meta[name="description"]');
      if (descriptionTag) {
        descriptionTag.setAttribute('content', metadata.description);
      }
    }
  }, [metadata]);

  const onSubmit = async (values) => {
    const currentToken = new URLSearchParams(location.search).get('token') || '';

    if (!currentToken) {
      setSubmitFeedback({
        message: content.form.errors.tokenMissing,
        isError: true,
      });
      return;
    }

    try {
      await confirmYowanPasswordSetup({
        token: currentToken,
        newPassword: values.new_password,
      });
      setSubmitFeedback({
        message: content.form.successMessage,
        isError: false,
      });
      navigate(appendYowanPreviewQueryIfNeeded('/login', location.search));
    } catch (error) {
      setSubmitFeedback({
        message: resolvePasswordSetupErrorMessage(error, content),
        isError: true,
      });
    }
  };

  return (
    <Section>
      <PageContainer>
        <div className="yowan-auth-support-shell">
          <div className="yowan-auth-support-panel c-surface-content">
            <div className="yowan-auth-support-panel__body">
              <h1 className="yowan-auth-support-panel__title">{content.header.title}</h1>
              {content.header.paragraphs.map((paragraph) => (
                <p key={paragraph} className="yowan-auth-support-panel__lead">
                  {paragraph}
                </p>
              ))}
            </div>

            <form className="yowan-auth-support-form c-form" onSubmit={handleSubmit(onSubmit)}>
              {!tokenFromQuery ? (
                <p className="yowan-auth-support-form__error">{content.form.errors.tokenMissing}</p>
              ) : null}

              <label className="c-form__field">
                <span className="c-form__label">
                  {content.form.labels.newPassword}
                  <em className="c-form__required">*</em>
                </span>
                <input
                  className="c-form__control"
                  type="password"
                  autoComplete="new-password"
                  {...register('new_password', {
                    required: content.form.errors.required,
                    minLength: {
                      value: 8,
                      message: content.form.errors.passwordTooShort,
                    },
                    maxLength: {
                      value: 128,
                      message: content.form.errors.passwordTooLong,
                    },
                    validate: (value) =>
                      !/^\d+$/.test(value || '') || content.form.errors.passwordNumericOnly,
                  })}
                />
                <span className="c-form__help yowan-auth-support-form__help">
                  {content.form.helpText.newPassword}
                </span>
                {errors.new_password ? (
                  <em className="yowan-auth-support-form__error">{errors.new_password.message}</em>
                ) : null}
              </label>

              <label className="c-form__field">
                <span className="c-form__label">
                  {content.form.labels.newPasswordConfirm}
                  <em className="c-form__required">*</em>
                </span>
                <input
                  className="c-form__control"
                  type="password"
                  autoComplete="new-password"
                  {...register('new_password_confirm', {
                    required: content.form.errors.required,
                    validate: (value) =>
                      value === watch('new_password') || content.form.errors.passwordMismatch,
                  })}
                />
                {errors.new_password_confirm ? (
                  <em className="yowan-auth-support-form__error">{errors.new_password_confirm.message}</em>
                ) : null}
              </label>

              <button type="submit" className="c-form__submit" disabled={isSubmitting || !tokenFromQuery}>
                {content.form.submitLabel}
              </button>

              {isSubmitting ? (
                <p className="yowan-auth-support-form__message yowan-auth-support-form__message--pending">
                  <LoadingSpinner inline size="sm" />
                  <span>{content.form.pendingMessage}</span>
                </p>
              ) : submitFeedback.message ? (
                <p
                  className={
                    submitFeedback.isError
                      ? 'yowan-auth-support-form__error'
                      : 'yowan-auth-support-form__message'
                  }
                >
                  {submitFeedback.message.split('\n').map((line, index) => (
                    <React.Fragment key={`${line}-${index}`}>
                      {line}
                      <br />
                    </React.Fragment>
                  ))}
                </p>
              ) : null}
            </form>
          </div>
        </div>
      </PageContainer>
    </Section>
  );
};

export default PasswordSetupConfirmPage;
